<template>
	<div class="mode-dropdown-wrapper" v-click-outside="closeDropdown">
		<!-- Header -->
        <div
        class="dropdown-header"
        :class="{ 'active': dropdownIsOpen }"
        @click="dropdownIsOpen = !dropdownIsOpen">
            <!-- Mode Icon -->
            <component class="mode-icon" :is="modeIcon" />
            <!-- Arrow Icon -->
            <arrow-down-icon class="arrow-icon" />
        </div>
		<!-- Body -->
        <div v-if="dropdownIsOpen" class="dropdown-body" :class="{'mobile': getMobileAppState}">
            <dropdown-row
            v-for="row in modeDropdownOptions"
            :appThemeMode="getAppThemeMode"
            :key="row.id"
            :selectedOption="selectedOption"
            :option="row"
            @clicked="rowClicked"/>
        </div>
	</div>
</template>

<script setup>
/* Vue */
import { ref, computed, defineAsyncComponent } from 'vue'

/* Components */
import ArrowDownIcon from '@/assets/icons/arrow-down.svg?inline'
import DropdownRow from '@/components/generic/dropdowns/mode/Row'
/* Store */
import { mapGetters } from '@/store/helpers/mappers'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['clicked'])

/** props received
 @selectedOption { String }
 */
const props = defineProps({
    selectedOption: [String, Number]
})

const { getAppThemeMode, getMobileAppState } = mapGetters()

const modeDropdownOptions = [{
	id: 'light',
	mode: t('dropdowns.mode.light'),
	icon: 'sun',
	description: t('dropdowns.mode.lightDescription')
},
{
	id: 'dark',
	mode: t('dropdowns.mode.dark'),
	icon: 'moon',
	description: t('dropdowns.mode.darkDescription')
},
{
	id: 'system',
	mode: t('dropdowns.mode.system'),
	description: t('dropdowns.mode.systemDescription')
}]

/* Data */
const dropdownIsOpen = ref(false)

const modeIcon = computed(() => {
    const icon = modeDropdownOptions.find(obj => obj.id === getAppThemeMode.value)?.icon
	return defineAsyncComponent(() => import('@/assets/icons/' + icon + '.svg?inline'))
})

const rowClicked = (rowId) => {
    emit('clicked', rowId)
    closeDropdown()
}

const closeDropdown = () => {
	dropdownIsOpen.value = false
}
</script>

<style lang="scss" scoped>
.mode-dropdown-wrapper {
    position: relative;

	width: 72px;
	height: 40px;

    border-radius: 24px;

    background-color: var(--secondary-light-2);

    .dropdown-header {
        display: flex;
        align-items: center;
        gap: 8px;

        width: 100%;
        height: 100%;

        padding: 0 16px;

        cursor: pointer;

        .mode-icon,
        .arrow-icon {
            flex-shrink: 0;
            pointer-events: none;
        }

        .mode-icon {
            :deep(.change-color-fill) {
                fill: var(--secondary-dark-4)
            }
        }

        .arrow-icon {
            pointer-events: none;
            transition: all .4s ease;

            :deep(.change-color-fill) {
                fill: var(--secondary-dark-2)
            }
        }

        &.active {
            .arrow-icon {
                transform: rotate(180deg);
            }
        }
    }

    .dropdown-body {
        width: 301px;

        position: absolute;
        top: 0;
        left: 0;

        border-radius: 8px;
        background-color: var(--secondary-light-4);
        box-shadow: var(--box-shadow-2);

        z-index: 1;

        &.mobile {
            left: unset;
            right: 0;
        }
    }
}
</style>
